/**
 * Speaking spoken updates in the browser (window.speechSynthesis). The Airview Studio iOS app (build 7+,
 * `sightlineNative.speaksCallouts`) speaks callouts itself from its own socket, so the web stays
 * silent there — no double speech.
 *
 * One line at a time; a line that waited more than 10 s is dropped (never a backlog). iOS Safari only
 * lets speech start after a user gesture, so the tap that turns spoken updates on also says a line.
 *
 * Voice: the most natural installed English voice (Premium / Enhanced / Neural / Natural over the
 * compact defaults — `voiceScore`), or the one picked in Spoken updates (remembered per device and
 * handed to the native app so it speaks with the same voice).
 */
import { getNative } from './native';

const nativeSpeech = getNative;

/** true when the native app speaks callouts itself. */
export const nativeSpeaksCallouts = (): boolean => nativeSpeech()?.speaksCallouts === true;

export const MAX_WAIT_MS = 10_000;

const synth = (): SpeechSynthesis | null => (typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null);

export const speechSupported = (): boolean => !!synth() || typeof nativeSpeech()?.speak === 'function';

interface Item {
  text: string;
  at: number;
}

const queue: Item[] = [];
let speaking = false;
let crewVoiceActive = false;
/** Crew voice takes priority; discard queued announcements rather than replaying a backlog. */
export function setCrewVoiceAudioActive(active:boolean):void {
  crewVoiceActive=active;
  nativeSpeech()?.setCrewVoiceActive?.(active);
  if(active){queue.length=0;synth()?.cancel();speaking=false;}
}
let watchdog: ReturnType<typeof setTimeout> | null = null;

// ───────────────────────────── voices ─────────────────────────────

/** The few voices worth hearing for minutes on end, as engines name them. */
const NATURAL_NAMES = /\b(ava|zoe|evan|samantha|allison|nathan|tom|susan|joelle|noelle|nicky|aaron|serena|daniel|karen|moira|tessa|kate|oliver|jenny|aria|guy|davis|jane|jason|sara|tony|nancy|libby|sonia|ryan|natasha|william)\b/i;
/** macOS / iOS novelty and legacy voices: robotic or silly in a field earpiece. */
const NOVELTY = /\b(albert|bad news|bahh|bells|boing|bubbles|cellos|good news|jester|organ|superstar|trinoids|whisper|wobble|zarvox|fred|junior|ralph|kathy|grandma|grandpa|rocko|shelley|flo|eddy|reed|sandy|espeak)\b/i;

export interface VoiceInfo {
  /** voiceURI — on Apple platforms the AVSpeechSynthesisVoice identifier (the native app can use it). */
  id: string;
  name: string;
  lang: string;
  /** Network voice (Google, Microsoft Online): sounds great, needs signal. */
  online: boolean;
  score: number;
}

/**
 * How natural a voice sounds (higher = better). English only (others score -Infinity). Premium /
 * Enhanced / Neural / Natural beat the compact defaults; a local voice gets a small bonus because
 * it still works with one bar of signal at the edge of a field.
 */
export function voiceScore(v: Pick<SpeechSynthesisVoice, 'name' | 'lang' | 'localService' | 'voiceURI'>, preferLang = 'en-US'): number {
  const lang = (v.lang ?? '').replace('_', '-');
  if (!/^en\b/i.test(lang)) return -Infinity;
  const id = `${v.name} ${v.voiceURI ?? ''}`;
  let s = 0;
  if (NOVELTY.test(v.name)) s -= 100;
  if (/premium/i.test(id)) s += 60;
  else if (/enhanced/i.test(id)) s += 50;
  if (/neural|natural/i.test(id)) s += 50; // Microsoft "… Online (Natural)", Edge / Windows neural voices
  if (/siri/i.test(id)) s += 40;
  if (/^google\b/i.test(v.name)) s += 35;
  if (NATURAL_NAMES.test(v.name)) s += 20;
  if (/compact/i.test(id)) s -= 10;
  if (/espeak|robot/i.test(id)) s -= 60;
  if (v.localService) s += 10;
  if (lang.toLowerCase() === preferLang.toLowerCase()) s += 15; // the listener's own accent first
  else if (lang.slice(0, 2).toLowerCase() === preferLang.slice(0, 2).toLowerCase()) s += 2;
  return s;
}

const preferredLang = (): string => {
  const l = typeof navigator !== 'undefined' ? navigator.language : '';
  return l && /^en\b/i.test(l) ? l : 'en-US';
};

/** English voices, best first. Engines load voices late: subscribe with onVoicesChanged. */
export function listVoices(): VoiceInfo[] {
  const s = synth();
  if (!s) return [];
  let voices: SpeechSynthesisVoice[] = [];
  try {
    voices = s.getVoices();
  } catch {
    return [];
  }
  const lang = preferredLang();
  const seen = new Set<string>();
  return voices
    .map((v) => ({ id: v.voiceURI || v.name, name: v.name, lang: v.lang, online: !v.localService, score: voiceScore(v, lang) }))
    .filter((v) => Number.isFinite(v.score) && !seen.has(v.id) && seen.add(v.id))
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
}

export function onVoicesChanged(cb: () => void): () => void {
  const s = synth();
  if (!s || typeof s.addEventListener !== 'function') return () => undefined;
  s.addEventListener('voiceschanged', cb);
  return () => s.removeEventListener('voiceschanged', cb);
}

const VOICE_KEY = 'apos.voice.v1';

/** The voice chosen on this device (voiceURI), or null = best available automatically. */
export function getVoicePref(): string | null {
  try {
    return localStorage.getItem(VOICE_KEY) || null;
  } catch {
    return null;
  }
}

/** Remember the voice for this device and tell the Airview Studio app (build 12+) to use it too. */
export function setVoicePref(id: string | null): void {
  try {
    if (id) localStorage.setItem(VOICE_KEY, id);
    else localStorage.removeItem(VOICE_KEY);
  } catch {
    /* private mode: this session only */
    sessionVoice = id;
  }
  syncNativeVoice();
}
let sessionVoice: string | null = null;

/** Hand the remembered voice to the native app (no-op on older builds and in browsers). */
export function syncNativeVoice(): void {
  const n = nativeSpeech();
  if (typeof n?.setVoice === 'function') n.setVoice(getVoicePref() ?? sessionVoice);
}

function pickVoice(s: SpeechSynthesis): SpeechSynthesisVoice | null {
  try {
    const voices = s.getVoices();
    const want = getVoicePref() ?? sessionVoice;
    if (want) {
      const v = voices.find((x) => (x.voiceURI || x.name) === want);
      if (v) return v;
    }
    const best = listVoices()[0];
    return (best && voices.find((x) => (x.voiceURI || x.name) === best.id)) ?? voices.find((v) => v.lang?.startsWith('en')) ?? null;
  } catch {
    return null;
  }
}

function next(): void {
  const s = synth();
  if (!s || speaking) return;
  let item: Item | undefined;
  while ((item = queue.shift())) if (Date.now() - item.at <= MAX_WAIT_MS) break;
  if (!item) return;
  speaking = true;
  const done = () => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = null;
    speaking = false;
    next();
  };
  try {
    const u = new SpeechSynthesisUtterance(item.text);
    const v = pickVoice(s);
    if (v) u.voice = v;
    // natural pace: engines' 1.0 is conversational; a touch quicker than that sounds rushed in an earpiece
    u.rate = 1;
    u.pitch = 1;
    u.onend = done;
    u.onerror = done;
    s.speak(u);
    // some engines never fire onend (backgrounded tab, interrupted audio)
    watchdog = setTimeout(done, 12_000);
  } catch {
    done();
  }
}

/** Queue a callout for speech (browser only; no-op when the native app speaks). */
export function speakCallout(text: string, receivedAt = Date.now()): void {
  if (!text || crewVoiceActive || nativeSpeaksCallouts()) return;
  queue.push({ text, at: receivedAt });
  next();
}

/**
 * Say a line NOW from a user gesture (switch on / "Test voice"): unlocks audio on iOS Safari; in the
 * native app the app speaks it (AirPods, silent switch).
 */
export function speakNow(text: string): boolean {
  if(crewVoiceActive)return false;
  const n = nativeSpeech();
  if (n?.speaksCallouts && typeof n.speak === 'function') {
    n.speak(text);
    return true;
  }
  const s = synth();
  if (!s) return false;
  try {
    queue.length = 0;
    s.cancel();
    speaking = false;
  } catch {
    /* ignore */
  }
  queue.push({ text, at: Date.now() });
  next();
  return true;
}

/** Tests. */
export function resetSpeechQueue(): void {
  crewVoiceActive=false;
  queue.length = 0;
  speaking = false;
  if (watchdog) clearTimeout(watchdog);
  watchdog = null;
}
